import React, { createContext, useReducer, ReactNode } from 'react';
import { IKeysBlankNames } from '../constants/pragerohlinge';
import { KeysState, KeysAction, KeysContextType } from './KeysContextTypes';
import { TSelectedKeyTypes } from '../store/keysTypes';

// Initial state
const initialState: KeysState = {
  page: 1,
  enabledTabIds: [1],
  selectedKeyTypes: {
    [IKeysBlankNames.FA]: [],
    [IKeysBlankNames.OV]: [],
    [IKeysBlankNames.ORIGINAL]: [],
  },
  selectedKeyId: null,
  modalName: null,
  mengeModalType: null,
  deletionId: null,
  comment: '',
};

// Reducer function
const keysReducer = (state: KeysState, action: KeysAction): KeysState => {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: action.payload };

    case 'ENABLE_TAB':
      return {
        ...state,
        enabledTabIds: state.enabledTabIds.includes(action.payload)
          ? state.enabledTabIds
          : [...state.enabledTabIds, action.payload],
      };

    case 'DISABLE_TAB':
      return {
        ...state,
        enabledTabIds: state.enabledTabIds.filter(id => id !== action.payload),
      };

    case 'SET_SELECTED_KEY_TYPES': {
      const { blank } = action.payload;
      if (!blank) return state;
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: [...state.selectedKeyTypes[blank], action.payload],
        },
      };
    }

    case 'REMOVE_SELECTED_KEY_TYPE': {
      const { value, blank } = action.payload;
      if (!blank || !value) return state;
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: state.selectedKeyTypes[blank].filter(item => item.value !== value),
        },
      };
    }

    case 'REMOVE_KEY_BY_ID': {
      const keyId = action.payload;
      const newSelectedKeyTypes = { ...state.selectedKeyTypes };
      
      // Find and remove the key from all blank types
      Object.entries(newSelectedKeyTypes).forEach(([blank, keys]) => {
        newSelectedKeyTypes[blank as keyof TSelectedKeyTypes] = keys.filter(key => key.id !== keyId);
      });
      
      return {
        ...state,
        selectedKeyTypes: newSelectedKeyTypes,
        // Clear selectedKeyId if the deleted key was selected
        selectedKeyId: state.selectedKeyId === keyId ? null : state.selectedKeyId,
      };
    }

    case 'SET_KEY_TYPES':
      return { ...state, selectedKeyTypes: action.payload };

    case 'SET_SELECTED_KEY_ID':
      return { ...state, selectedKeyId: action.payload };

    case 'SET_VISITED': {
      const { keyIndex, visited, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = { ...updatedKeys[keyIndex], visited };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_KEY_MENGE': {
      const { keyIndex, value, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = { ...updatedKeys[keyIndex], menge: value };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_KEY_SIDE_TYPE': {
      const { keyIndex, side, type, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = {
        ...updatedKeys[keyIndex],
        [side]: { ...updatedKeys[keyIndex][side], type },
      };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_KEY_TEXT_FIELD': {
      const { keyIndex, side, textIndex, value, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      const updatedText = [...updatedKeys[keyIndex][side].text];
      updatedText[textIndex] = { ...updatedText[textIndex], value };
      
      updatedKeys[keyIndex] = {
        ...updatedKeys[keyIndex],
        [side]: { ...updatedKeys[keyIndex][side], text: updatedText },
      };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_KEY_FONT': {
      const { keyIndex, side, value, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = {
        ...updatedKeys[keyIndex],
        [side]: { ...updatedKeys[keyIndex][side], font: value },
      };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_KEY_FONT_SIZE': {
      const { keyIndex, name, side, value, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank]?.[keyIndex]?.[side]?.size?.[name]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = {
        ...updatedKeys[keyIndex],
        [side]: {
          ...updatedKeys[keyIndex][side],
          size: { ...updatedKeys[keyIndex][side].size, [name]: value },
        },
      };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_KEY_LOGO': {
      const { keyIndex, side, value, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = {
        ...updatedKeys[keyIndex],
        [side]: { ...updatedKeys[keyIndex][side], logo: value },
      };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_KEY_LOGO_SIZE': {
      const { keyIndex, side, value, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = {
        ...updatedKeys[keyIndex],
        [side]: { ...updatedKeys[keyIndex][side], logoSize: value },
      };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_KEY_IMAGE': {
      const { keyIndex, side, value, blank } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = {
        ...updatedKeys[keyIndex],
        [side]: { ...updatedKeys[keyIndex][side], image: value },
      };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_SCREENSHOT_READY': {
      const { keyIndex, blank, side, value } = action.payload;
      if (!blank || !state.selectedKeyTypes[blank][keyIndex]) return state;
      
      const updatedKeys = [...state.selectedKeyTypes[blank]];
      updatedKeys[keyIndex] = {
        ...updatedKeys[keyIndex],
        [side]: { ...updatedKeys[keyIndex][side], ready: value },
      };
      
      return {
        ...state,
        selectedKeyTypes: {
          ...state.selectedKeyTypes,
          [blank]: updatedKeys,
        },
      };
    }

    case 'SET_MODAL_NAME':
      return { ...state, modalName: action.payload };

    case 'SET_MENGE_MODAL_TYPE':
      return { ...state, mengeModalType: action.payload };

    case 'SET_DELETION_ID':
      return { ...state, deletionId: action.payload };

    case 'SET_COMMENT':
      return { ...state, comment: action.payload };

    case 'RESET_KEYS':
      return initialState;

    default:
      return state;
  }
};

// Create context
export const KeysContext = createContext<KeysContextType | undefined>(undefined);

// Provider component
interface KeysProviderProps {
  children: ReactNode;
}

export const KeysProvider: React.FC<KeysProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(keysReducer, initialState);

  return (
    <KeysContext.Provider value={{ state, dispatch }}>
      {children}
    </KeysContext.Provider>
  );
};